import { scriptLoader } from "./script-loader";
import { loggerService } from "./logger-service";

class ThreatMetrixService {
  private generatedSessionId: string | null = null;
  private registeredSessionId: string | null = null;

  isInitialized = () => Boolean(this.generatedSessionId);

  /**
   * Generates a session ID if one doesn't exist already
   * @private Internal method for ID generation
   */
  private generateSessionId(): string {
    if (!this.generatedSessionId) {
      // Create a new session ID
      const chars =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      const len = chars.length;
      this.generatedSessionId = " "
        .repeat(32)
        .replaceAll(" ", () => chars.charAt((Math.random() * len) | 0));
    }
    return this.generatedSessionId;
  }

  /**
   * Registers the current device with ThreatMetrix by loading the required script
   * @returns Promise that resolves when the registration is complete
   */
  registerDevice(): Promise<void> {
    if (this.registeredSessionId) return Promise.resolve();

    const sessionId = this.generateSessionId();

    return scriptLoader
      .loadScript(
        "https://tm.bdc-cdn.com/fp/tags.js?org_id=ceurt9zj&session_id=" +
          sessionId
      )
      .then(() => {
        this.registeredSessionId = sessionId;
      })
      .catch((error) => {
        loggerService.logError("TMX: Device registration failed", {
          error: error?.toString(),
          sessionIdPrefix: this.getMaskedSessionId(),
        });
        return Promise.resolve();
      });
  }
  /**
   * Gets the ThreatMetrix session ID
   * Throws an error if called before initialization
   * Logs a warning if the device is not registered with ThreatMetrix
   * @param context - Additional context to include in the log (e.g., where this was called from)
   * @returns The ThreatMetrix session ID
   * @throws Error if called before registerDevice()
   */
  getSessionId(context: string = "generic"): string {
    if (!this.generatedSessionId) {
      throw new Error(
        `TMX: getSessionId called before initialization (${context})`
      );
    }

    // Return the session ID we sent to ThreatMetrix (even if registration failed)
    // This respects the intent to always send the same ID we sent to ThreatMetrix
    const sessionId = this.registeredSessionId || this.generatedSessionId;

    // Log warning if using the fallback ID (registration failed)
    // Sending a generated device session ID without successful loading of TMX, when ESUS calls Sentinel downstream,
    // it'll return a default score which means we now potentially allow sanctioned countries to pollute our database(s) and/or service(s)
    if (!this.registeredSessionId) {
      loggerService.logWarning(
        `TMX: Using session ID with device not registered (${context})`,
        {
          sessionIdPrefix: this.getMaskedSessionId(),
          context,
        }
      );
    }

    return sessionId;
  }

  /**
   * Returns a masked version of the current session ID for secure logging
   * @returns Masked ID string or null if no ID is available
   */
  getMaskedSessionId(): string | null {
    const id = this.registeredSessionId || this.generatedSessionId;
    if (!id) return null;

    return `${id.substring(0, 2)}****${id.substring(id.length - 2)}`;
  }
}

export const threatmetrixService = new ThreatMetrixService();
